import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Phone,
  Users,
  CheckSquare,
  Flag,
  Mail,
  Utensils,
  Clock,
  Calendar,
  User,
  FileText,
  X,
} from "lucide-react";
import { toast } from "sonner";
import UserSelectByPermissions from "features/user-select-by-permission/user-select-by-permission";
import DatePicker from "features/datepicker/date-picker";
import {
  patchProjectActivity,
  ProjectActivity,
  ProjectActivityType,
} from "services/project-activities.service";
import {
  ActivityFormState,
  activityToFormState,
  combineLocal,
  toYmd,
} from "components/project/project-activity-form-utils";

const ACTIVITY_TYPES: Array<{
  id: ProjectActivityType;
  labelKey: string;
  defaultLabel: string;
  Icon: typeof Phone;
}> = [
  { id: "CALL", labelKey: "project_activity_call", defaultLabel: "Call", Icon: Phone },
  {
    id: "MEETING",
    labelKey: "project_activity_meeting",
    defaultLabel: "Meeting",
    Icon: Users,
  },
  {
    id: "TASK",
    labelKey: "project_activity_task",
    defaultLabel: "Task",
    Icon: CheckSquare,
  },
  {
    id: "DEADLINE",
    labelKey: "project_activity_deadline",
    defaultLabel: "Deadline",
    Icon: Flag,
  },
  {
    id: "EMAIL",
    labelKey: "project_activity_email",
    defaultLabel: "Email",
    Icon: Mail,
  },
  {
    id: "LUNCH",
    labelKey: "project_activity_lunch",
    defaultLabel: "Lunch",
    Icon: Utensils,
  },
];

type Props = {
  open: boolean;
  companyId: string;
  projectId: string;
  activity: ProjectActivity | null;
  onClose: () => void;
  onSaved: (activity: ProjectActivity) => void;
};

export default function ProjectActivityEditModal({
  open,
  companyId,
  projectId,
  activity,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ActivityFormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && activity) {
      setForm(activityToFormState(activity));
    } else if (!open) {
      setForm(null);
    }
  }, [open, activity]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !activity || !form) return null;

  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) {
      toast.error(
        t("project_activity_title_required", {
          defaultValue: "Title is required.",
        })
      );
      return;
    }
    setSaving(true);
    try {
      const updated = await patchProjectActivity(
        companyId,
        projectId,
        activity.id,
        {
          title,
          activityType: form.activityType,
          startAt: combineLocal(form.startDate, form.startTime),
          endAt: combineLocal(form.endDate, form.endTime),
          priority: form.priority || null,
          note: form.note.trim() || null,
          location: form.location.trim() || null,
          videoCallUrl: form.videoCallUrl.trim() || null,
          availability: form.availability,
          assignedTo: form.assignedTo || null,
          isDone: form.isDone,
        }
      );
      toast.success(
        t("project_activity_updated", { defaultValue: "Activity updated." })
      );
      onSaved(updated);
      onClose();
    } catch {
      toast.error(
        t("project_activity_save_failed", {
          defaultValue: "Could not save activity.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-activity-edit-title"
        className="relative z-[91] flex max-h-[min(90dvh,720px)] w-full max-w-xl flex-col overflow-hidden rounded-t-xl border border-zinc-200 sm:rounded-xl bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-950"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <h2
            id="project-activity-edit-title"
            className="text-base font-semibold text-zinc-900 dark:text-zinc-100"
          >
            {t("project_activity_edit", { defaultValue: "Edit activity" })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 max-md:h-11 max-md:w-11 dark:hover:bg-zinc-800"
            aria-label={t("close", { defaultValue: "Close" })}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
            <input
              type="text"
              value={form.title}
              onChange={(e) =>
                setForm((prev) =>
                  prev ? { ...prev, title: e.target.value } : prev
                )
              }
              className="w-full border-0 bg-transparent text-lg font-semibold text-zinc-900 outline-none dark:text-zinc-100"
            />
          </div>

          <div className="flex flex-wrap gap-1 border-b border-zinc-200 px-3 py-2 dark:border-zinc-700">
            {ACTIVITY_TYPES.map(({ id, labelKey, defaultLabel, Icon }) => {
              const selected = form.activityType === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    setForm((prev) =>
                      prev ? { ...prev, activityType: id } : prev
                    )
                  }
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-2.5 text-xs font-semibold max-md:min-h-11 lg:py-1.5 ${
                    selected
                      ? "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200"
                      : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={14} strokeWidth={1.75} />
                  {t(labelKey, { defaultValue: defaultLabel })}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 px-4 py-3">
            <div className="flex items-start gap-3">
              <Clock className="mt-2.5 h-4 w-4 shrink-0 text-zinc-400" />
              <div className="flex min-w-0 flex-1 flex-wrap items-end gap-2">
                <div className="min-w-[9.5rem]">
                  <DatePicker
                    selected={form.startDate || null}
                    title={t("start_date", { defaultValue: "Start date" })}
                    fullWidth={false}
                    disableDatesAfter={false}
                    onChange={(d: Date | null) =>
                      setForm((prev) =>
                        prev ? { ...prev, startDate: toYmd(d) } : prev
                      )
                    }
                  />
                </div>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev ? { ...prev, startTime: e.target.value } : prev
                    )
                  }
                  className="rounded border border-zinc-300 bg-white px-2 py-1.5 text-base lg:text-sm dark:border-zinc-600 dark:bg-zinc-900"
                />
                <span className="mb-2 text-zinc-400">–</span>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev ? { ...prev, endTime: e.target.value } : prev
                    )
                  }
                  className="rounded border border-zinc-300 bg-white px-2 py-1.5 text-base lg:text-sm dark:border-zinc-600 dark:bg-zinc-900"
                />
                <div className="min-w-[9.5rem]">
                  <DatePicker
                    selected={form.endDate || null}
                    title={t("end_date", { defaultValue: "End date" })}
                    fullWidth={false}
                    disableDatesAfter={false}
                    onChange={(d: Date | null) =>
                      setForm((prev) =>
                        prev ? { ...prev, endDate: toYmd(d) } : prev
                      )
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 shrink-0 text-zinc-400" />
              <select
                value={form.priority}
                onChange={(e) =>
                  setForm((prev) =>
                    prev
                      ? {
                          ...prev,
                          priority: e.target
                            .value as ActivityFormState["priority"],
                        }
                      : prev
                  )
                }
                className="min-w-[10rem] rounded border border-zinc-300 bg-white px-2 py-1.5 text-base lg:text-sm dark:border-zinc-600 dark:bg-zinc-900"
              >
                <option value="">
                  {t("project_activity_priority", { defaultValue: "Priority" })}
                </option>
                <option value="LOW">
                  {t("project_activity_priority_low", { defaultValue: "Low" })}
                </option>
                <option value="MEDIUM">
                  {t("project_activity_priority_medium", {
                    defaultValue: "Medium",
                  })}
                </option>
                <option value="HIGH">
                  {t("project_activity_priority_high", {
                    defaultValue: "High",
                  })}
                </option>
              </select>
            </div>

            <div className="flex flex-col gap-2 pl-7">
              <input
                type="text"
                value={form.location}
                onChange={(e) =>
                  setForm((prev) =>
                    prev ? { ...prev, location: e.target.value } : prev
                  )
                }
                placeholder={t("project_activity_location", {
                  defaultValue: "Location",
                })}
                className="rounded border border-zinc-300 px-2 py-1.5 text-base lg:text-sm dark:border-zinc-600 dark:bg-zinc-900"
              />
              <input
                type="url"
                value={form.videoCallUrl}
                onChange={(e) =>
                  setForm((prev) =>
                    prev ? { ...prev, videoCallUrl: e.target.value } : prev
                  )
                }
                placeholder={t("project_activity_video", {
                  defaultValue: "Video call URL",
                })}
                className="rounded border border-zinc-300 px-2 py-1.5 text-base lg:text-sm dark:border-zinc-600 dark:bg-zinc-900"
              />
            </div>

            <div className="flex items-start gap-3">
              <FileText className="mt-2 h-4 w-4 shrink-0 text-zinc-400" />
              <textarea
                value={form.note}
                onChange={(e) =>
                  setForm((prev) =>
                    prev ? { ...prev, note: e.target.value } : prev
                  )
                }
                rows={4}
                placeholder={t("project_activity_note_placeholder", {
                  defaultValue: "Notes",
                })}
                className="min-h-[96px] w-full resize-y rounded border border-zinc-300 bg-[#fffbeb] px-3 py-2 text-base outline-none lg:text-sm dark:border-zinc-600 dark:bg-amber-950/20"
              />
            </div>

            <div className="flex items-center gap-3">
              <User className="h-4 w-4 shrink-0 text-zinc-400" />
              <div className="min-w-0 flex-1">
                <UserSelectByPermissions
                  value={form.assignedTo || undefined}
                  onChange={(val: string) =>
                    setForm((prev) =>
                      prev ? { ...prev, assignedTo: val || null } : prev
                    )
                  }
                  label="select_user"
                  Permission="Access project"
                />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-zinc-800 dark:text-zinc-200">
              <input
                type="checkbox"
                checked={form.isDone}
                onChange={(e) =>
                  setForm((prev) =>
                    prev ? { ...prev, isDone: e.target.checked } : prev
                  )
                }
                className="rounded border-zinc-300"
              />
              {t("project_activity_mark_done", { defaultValue: "Mark as done" })}
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))] dark:border-zinc-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 disabled:opacity-50 max-md:min-h-11 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50 max-md:min-h-11"
          >
            {saving
              ? t("saving", { defaultValue: "Saving…" })
              : t("save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
